import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// OFAC Types
export interface OFACMatchedEntity {
  name: string;
  program: string[];
  remarks?: string;
  address?: string;
  type?: 'individual' | 'entity';
  aliases?: string[];
  dateOfBirth?: string;
  placeOfBirth?: string;
  sanctions_date?: string;
}

export interface OFACScreeningResult {
  vendorUei: string;
  vendorName: string;
  matchStatus: 'clear' | 'possible_match' | 'sanctioned' | 'screening_error';
  matchScore: number;
  matchedEntity?: OFACMatchedEntity;
  screenedAt: string;
  screeningId?: string;
  falsePositiveReported?: boolean;
  lastUpdated?: string;
  notes?: string;
}

export interface OFACSyncStatus {
  lastSync: string | null;
  syncInProgress: boolean;
  totalRecords: number;
  lastUpdateCount: number;
  version: string;
  nextScheduledSync?: string;
}

export interface OFACComplianceStats {
  totalScreened: number;
  clearVendors: number;
  possibleMatches: number;
  sanctionedVendors: number;
  pendingScreening: number;
  falsePositivesReported: number;
  lastScreeningDate?: string;
}

export interface OFACBulkScreeningJob {
  jobId: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  totalVendors: number;
  completedVendors: number;
  failedVendors: number;
  startedAt: string;
  completedAt?: string;
  results?: OFACScreeningResult[];
}

interface OFACState {
  // Screening results cache
  screeningResults: Record<string, OFACScreeningResult>;

  // UI state
  loading: boolean;
  bulkScreeningLoading: boolean;
  error: string | null;

  // Sync status
  syncStatus: OFACSyncStatus;

  // Compliance statistics
  complianceStats: OFACComplianceStats;

  // Bulk operations
  bulkJobs: Record<string, OFACBulkScreeningJob>;

  // Settings
  settings: {
    autoScreenOnSearch: boolean;
    cacheExpiryHours: number;
    matchThreshold: number;
    enableNotifications: boolean;
  };

  // Recent alerts
  recentAlerts: Array<{
    id: string;
    vendorUei: string;
    vendorName: string;
    alertType: 'new_match' | 'sanctions_change' | 'sync_update';
    timestamp: string;
    read: boolean;
  }>;
}

const initialState: OFACState = {
  screeningResults: {},
  loading: false,
  bulkScreeningLoading: false,
  error: null,
  syncStatus: {
    lastSync: null,
    syncInProgress: false,
    totalRecords: 0,
    lastUpdateCount: 0,
    version: '1.0.0'
  },
  complianceStats: {
    totalScreened: 0,
    clearVendors: 0,
    possibleMatches: 0,
    sanctionedVendors: 0,
    pendingScreening: 0,
    falsePositivesReported: 0
  },
  bulkJobs: {},
  settings: {
    autoScreenOnSearch: true,
    cacheExpiryHours: 24,
    matchThreshold: 0.85,
    enableNotifications: true
  },
  recentAlerts: []
};

// Async thunks
export const screenVendor = createAsyncThunk(
  'ofac/screenVendor',
  async ({ uei, name }: { uei: string; name: string }, { rejectWithValue }) => {
    try {
      // This will be implemented when backend OFAC API is ready
      const response = await fetch(`/api/v1/vendor/ofac-screen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uei, name })
      });

      if (!response.ok) {
        throw new Error(`Screening failed: ${response.status}`);
      }

      const result = await response.json();
      return result as OFACScreeningResult;
    } catch (error: any) {
      return rejectWithValue(error.message || 'OFAC screening failed');
    }
  }
);

export const batchScreenVendors = createAsyncThunk(
  'ofac/batchScreen',
  async (vendors: Array<{ uei: string; name: string }>, { rejectWithValue }) => {
    try {
      const response = await fetch(`/api/v1/vendor/batch-ofac-screen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vendors })
      });

      if (!response.ok) {
        throw new Error(`Batch screening failed: ${response.status}`);
      }

      const result = await response.json();
      return result as OFACBulkScreeningJob;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Batch OFAC screening failed');
    }
  }
);

export const getOFACStatus = createAsyncThunk(
  'ofac/getStatus',
  async (uei: string, { rejectWithValue }) => {
    try {
      const response = await fetch(`/api/v1/vendor/${uei}/ofac-status`);

      if (!response.ok) {
        throw new Error(`Status check failed: ${response.status}`);
      }

      const result = await response.json();
      return result as OFACScreeningResult;
    } catch (error: any) {
      return rejectWithValue(error.message || 'OFAC status check failed');
    }
  }
);

export const syncOFACData = createAsyncThunk(
  'ofac/syncData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`/api/v1/admin/ofac-sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Sync failed: ${response.status}`);
      }

      const result = await response.json();
      return result as OFACSyncStatus;
    } catch (error: any) {
      return rejectWithValue(error.message || 'OFAC data sync failed');
    }
  }
);

export const reportFalsePositive = createAsyncThunk(
  'ofac/reportFalsePositive',
  async ({ uei, details }: { uei: string; details: any }, { rejectWithValue }) => {
    try {
      const response = await fetch(`/api/v1/vendor/ofac-false-positive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uei, details })
      });

      if (!response.ok) {
        throw new Error(`Report failed: ${response.status}`);
      }

      const result = await response.json();
      return { uei, ...result };
    } catch (error: any) {
      return rejectWithValue(error.message || 'False positive report failed');
    }
  }
);

export const getComplianceStats = createAsyncThunk(
  'ofac/getComplianceStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`/api/v1/admin/ofac-compliance-stats`);

      if (!response.ok) {
        throw new Error(`Stats fetch failed: ${response.status}`);
      }

      const result = await response.json();
      return result as OFACComplianceStats;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Compliance stats fetch failed');
    }
  }
);

const ofacSlice = createSlice({
  name: 'ofac',
  initialState,
  reducers: {
    // Cache management
    clearScreeningCache: (state) => {
      state.screeningResults = {};
    },

    removeFromCache: (state, action: PayloadAction<string>) => {
      delete state.screeningResults[action.payload];
    },

    updateCacheSettings: (state, action: PayloadAction<Partial<OFACState['settings']>>) => {
      state.settings = { ...state.settings, ...action.payload };
    },

    // Alert management
    markAlertAsRead: (state, action: PayloadAction<string>) => {
      const alert = state.recentAlerts.find(a => a.id === action.payload);
      if (alert) {
        alert.read = true;
      }
    },

    clearAlerts: (state) => {
      state.recentAlerts = [];
    },

    addAlert: (state, action: PayloadAction<Omit<OFACState['recentAlerts'][0], 'id' | 'timestamp' | 'read'>>) => {
      const alert = {
        ...action.payload,
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        read: false
      };
      state.recentAlerts.unshift(alert);
      // Keep only last 100 alerts
      if (state.recentAlerts.length > 100) {
        state.recentAlerts = state.recentAlerts.slice(0, 100);
      }
    },

    // Error handling
    clearError: (state) => {
      state.error = null;
    },

    // Manual result update (for testing)
    setScreeningResult: (state, action: PayloadAction<OFACScreeningResult>) => {
      state.screeningResults[action.payload.vendorUei] = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Screen vendor
      .addCase(screenVendor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(screenVendor.fulfilled, (state, action) => {
        state.loading = false;
        state.screeningResults[action.payload.vendorUei] = action.payload;

        // Add alert for sanctions matches
        if (action.payload.matchStatus === 'sanctioned' || action.payload.matchStatus === 'possible_match') {
          state.recentAlerts.unshift({
            id: Date.now().toString(),
            vendorUei: action.payload.vendorUei,
            vendorName: action.payload.vendorName,
            alertType: 'new_match',
            timestamp: new Date().toISOString(),
            read: false
          });
        }
      })
      .addCase(screenVendor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'OFAC screening failed';
      })

      // Batch screening
      .addCase(batchScreenVendors.pending, (state) => {
        state.bulkScreeningLoading = true;
        state.error = null;
      })
      .addCase(batchScreenVendors.fulfilled, (state, action) => {
        state.bulkScreeningLoading = false;
        state.bulkJobs[action.payload.jobId] = action.payload;
      })
      .addCase(batchScreenVendors.rejected, (state, action) => {
        state.bulkScreeningLoading = false;
        state.error = action.payload as string || 'Batch screening failed';
      })

      // Get OFAC status
      .addCase(getOFACStatus.fulfilled, (state, action) => {
        state.screeningResults[action.payload.vendorUei] = action.payload;
      })

      // Sync OFAC data
      .addCase(syncOFACData.pending, (state) => {
        state.syncStatus.syncInProgress = true;
        state.error = null;
      })
      .addCase(syncOFACData.fulfilled, (state, action) => {
        state.syncStatus = { ...state.syncStatus, ...action.payload, syncInProgress: false };

        // Add sync alert
        state.recentAlerts.unshift({
          id: Date.now().toString(),
          vendorUei: '',
          vendorName: 'System',
          alertType: 'sync_update',
          timestamp: new Date().toISOString(),
          read: false
        });
      })
      .addCase(syncOFACData.rejected, (state, action) => {
        state.syncStatus.syncInProgress = false;
        state.error = action.payload as string || 'OFAC sync failed';
      })

      // Report false positive
      .addCase(reportFalsePositive.fulfilled, (state, action) => {
        const result = state.screeningResults[action.payload.uei];
        if (result) {
          result.falsePositiveReported = true;
          result.notes = action.payload.notes || result.notes;
        }
      })

      // Get compliance stats
      .addCase(getComplianceStats.fulfilled, (state, action) => {
        state.complianceStats = action.payload;
      });
  }
});

export const {
  clearScreeningCache,
  removeFromCache,
  updateCacheSettings,
  markAlertAsRead,
  clearAlerts,
  addAlert,
  clearError,
  setScreeningResult
} = ofacSlice.actions;

export default ofacSlice.reducer;

// Selectors
export const selectOFACScreeningResult = (state: { ofac: OFACState }, uei: string) =>
  state.ofac.screeningResults[uei];

export const selectOFACStats = (state: { ofac: OFACState }) => state.ofac.complianceStats;

export const selectUnreadAlerts = (state: { ofac: OFACState }) =>
  state.ofac.recentAlerts.filter(alert => !alert.read);

export const selectOFACSyncStatus = (state: { ofac: OFACState }) => state.ofac.syncStatus;

export const selectOFACSettings = (state: { ofac: OFACState }) => state.ofac.settings;